/**
 * オーディオファイルの読み込み（デコード）と、波形表示用のピーク計算。
 */

export interface LoadedAudio {
  buffer: AudioBuffer;
  fileName: string;
}

/**
 * File を AudioBuffer にデコードする。デコード専用の一時 AudioContext を
 * 使う（suspended 状態でもデコードは可能なため autoplay ポリシーの影響を
 * 受けない）。
 */
export const decodeAudioFile = async (file: File): Promise<AudioBuffer> => {
  const arrayBuffer = await file.arrayBuffer();
  const ctx = new AudioContext();
  try {
    return await ctx.decodeAudioData(arrayBuffer);
  } finally {
    void ctx.close();
  }
};

export interface WaveformPeaks {
  min: Float32Array;
  max: Float32Array;
}

/** 1 カラムあたりの走査サンプル数の上限（長尺ファイルの描画負荷対策） */
const MAX_SAMPLES_PER_COLUMN = 2000;

/**
 * 波形描画用に、全チャンネル合成の min/max ピークをカラム数ぶん計算する。
 */
export const computePeaks = (
  buffer: AudioBuffer,
  columns: number,
): WaveformPeaks => {
  const min = new Float32Array(columns);
  const max = new Float32Array(columns);
  const frames = buffer.length;
  const channels: Float32Array[] = [];
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    channels.push(buffer.getChannelData(ch));
  }

  for (let col = 0; col < columns; col++) {
    const startFrame = Math.floor((col / columns) * frames);
    const endFrame = Math.max(
      startFrame + 1,
      Math.floor(((col + 1) / columns) * frames),
    );
    const stride = Math.max(
      1,
      Math.floor((endFrame - startFrame) / MAX_SAMPLES_PER_COLUMN),
    );
    let lo = Infinity;
    let hi = -Infinity;
    for (const data of channels) {
      for (let i = startFrame; i < endFrame; i += stride) {
        const v = data[i];
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
    min[col] = Number.isFinite(lo) ? lo : 0;
    max[col] = Number.isFinite(hi) ? hi : 0;
  }

  return { min, max };
};

/** 秒を「m:ss.mmm」表記にする（波形の位置readout用） */
export const formatSec = (sec: number): string => {
  const clamped = Math.max(0, sec);
  const m = Math.floor(clamped / 60);
  const s = Math.floor(clamped % 60);
  const ms = Math.floor((clamped % 1) * 1000);
  return `${m}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
};
